import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../../contexts/AuthContext'
import CommunityService from '../../../../service/CommunityService'
import VoiceChat from '../../../../components/VoiceChat/VoiceChat'
import EmojiPicker from '../../../../components/EmojiPicker/EmojiPicker'
import AuthModal from '../../../../components/AuthModal/AuthModal'
import styles from '../CommunityPage.module.scss'
import { CommentIcon, EyeIcon, LockIcon, MicrophoneIcon } from '../../../../components/CustomIcons'

const formatDate = (dateStr) => {
    if (!dateStr) return 'Недавно'
    const s = String(dateStr)
    // Handle DD.MM.YYYY HH:MM (DRF DATETIME_FORMAT)
    const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/)
    if (m) {
        const d = new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}`)
        if (!isNaN(d.getTime())) return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    }
    const d = new Date(s.replace(' ', 'T'))
    if (isNaN(d.getTime())) return 'Недавно'
    return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function useScrollHold(ref, dir) {
    const intervalRef = useRef(null)
    const start = () => {
        if (intervalRef.current) return
        intervalRef.current = setInterval(() => {
            ref.current?.scrollBy({ top: dir * 40, behavior: 'smooth' })
        }, 60)
    }
    const stop = () => {
        clearInterval(intervalRef.current)
        intervalRef.current = null
    }
    return { onMouseDown: start, onMouseUp: stop, onMouseLeave: stop, onTouchStart: start, onTouchEnd: stop }
}

export default function ForumThreadPage() {
    const { forumId, threadId } = useParams()
    const navigate = useNavigate()
    const { isAuthenticated, user } = useAuth()
    const [thread, setThread] = useState(null)
    const [posts, setPosts] = useState([])
    const [newPostContent, setNewPostContent] = useState('')
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('posts')
    const [showAuthModal, setShowAuthModal] = useState(false)
    const postsListRef = useRef(null)
    const holdUp = useScrollHold(postsListRef, -1)
    const holdDown = useScrollHold(postsListRef, 1)

    useEffect(() => {
        loadThread()
    }, [threadId])

    const loadThread = async () => {
        try {
            const [threadRes, postsRes] = await Promise.all([
                CommunityService.getForumThread(threadId),
                CommunityService.getForumPosts(threadId)
            ])
            setThread(threadRes.data)
            setPosts(postsRes.data?.results || postsRes.data || [])
        } catch (e) {
            console.error('Error loading thread', e)
        } finally {
            setLoading(false)
        }
    }

    const handleCreatePost = async () => {
        if (!newPostContent.trim()) return
        await CommunityService.createForumPost(threadId, { content: newPostContent })
        setNewPostContent('')
        const r = await CommunityService.getForumPosts(threadId)
        setPosts(r.data?.results || r.data || [])
    }

    const openAuthModal = (e) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        setShowAuthModal(true)
    }

    if (loading) {
        return (
            <div className={styles.communityPage}>
                <div className={styles.loading}>Загрузка темы...</div>
            </div>
        )
    }

    return (
        <div className={styles.communityPage}>
            <div className={styles.threadView}>
                <div className={styles.threadViewHeader}>
                    <button
                        className={styles.backButton}
                        onClick={() => navigate(-1)}
                    >
                        ← Назад к темам
                    </button>
                    <div className={styles.threadInfo}>
                        <h3>{thread?.title}</h3>
                        <div className={styles.threadMeta}>
                            <span>👤 Автор: {thread?.user?.username}</span>
                            <span><CommentIcon size={16} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{posts.length} ответов</span>
                            <span>🕒 {formatDate(thread?.created_at)}</span>
                        </div>
                    </div>
                </div>

                {/* Mobile tab bar */}
                <div className={styles.mobileTabs}>
                    <button
                        className={`${styles.mobileTab} ${activeTab === 'posts' ? styles.mobileTabActive : ''}`}
                        onClick={() => setActiveTab('posts')}
                    >
                        💬 Ответы {posts.length > 0 && <span className={styles.mobileTabCount}>{posts.length}</span>}
                    </button>
                    <button
                        className={`${styles.mobileTab} ${activeTab === 'voice' ? styles.mobileTabActive : ''}`}
                        onClick={() => setActiveTab('voice')}
                    >
                        🎙️ Голосовой чат
                    </button>
                </div>

                <div className={styles.threadLayout}>



                    {/* RIGHT: Voice Chat */}
                    <div className={`${styles.threadVoiceChatPanel} ${activeTab !== 'voice' ? styles.mobileHidden : ''}`}>
                        <div className={styles.threadVoiceChatLabel}>
                            <MicrophoneIcon size={13} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                            Голосовой чат темы
                        </div>
                        <div className={styles.threadVoiceChatInner}>
                            <VoiceChat
                                roomSlug={`ft-${threadId}`}
                                currentUser={user}
                                isOwner={false}
                                members={[]}
                            />
                        </div>
                    </div>
                    {/* LEFT: posts + reply */}
                    <div className={`${styles.threadPostsPanel} ${activeTab !== 'posts' ? styles.mobileHidden : ''}`}>
                        <div className={styles.threadPostsHeader}>
                            <div className={styles.threadPostsHeaderIcon}>💬</div>
                            <div>
                                <h4>Ответы</h4>
                                <p>Здесь только важные мысли и лучшие ответы по теме. Для живого общения используй голосовой чат справа.</p>
                            </div>
                        </div>
                        <div className={styles.postsListWrapper}>
            <div className={styles.forumPostsList} ref={postsListRef} data-lenis-prevent>
                            {posts.length === 0 && (
                                <div className={styles.emptyState}>
                                    <div className={styles.emptyIcon}>💬</div>
                                    <h3>Пока нет ответов</h3>
                                    <p>Будьте первым, кто ответит в этой теме</p>
                                </div>
                            )}
                            {posts.map((post, index) => {
                                const avatarUrl = post.user?.avatar
                                    ? (post.user.avatar.startsWith('http') ? post.user.avatar : `https://security.ncore.kg${post.user.avatar}`)
                                    : `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.username || 'User')}&background=E91E63&color=fff&bold=true`

                                return (
                                    <div key={post.id} className={`${styles.forumPost} ${post.is_solution ? styles.forumPostSolution : ''}`}>
                                        <div className={styles.forumPostLeft}>
                                            <img
                                                src={avatarUrl}
                                                alt={post.user?.username}
                                                className={styles.forumPostAvatar}
                                                onError={(e) => {
                                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(post.user?.username || 'User')}&background=E91E63&color=fff&bold=true`
                                                }}
                                            />
                                            <div className={styles.forumPostThread} />
                                            <span className={styles.forumPostNumber}>#{index + 1}</span>
                                        </div>
                                        <div className={styles.forumPostRight}>
                                            <div className={styles.forumPostHeader}>
                                                <div className={styles.forumPostMeta}>
                                                    <span className={styles.forumPostUsername}>{post.user?.username || 'Unknown'}</span>
                                                    <span className={styles.forumPostDate}>{formatDate(post.created_at)}</span>
                                                    {index === 0 && <span className={styles.opBadge}>Автор</span>}
                                                    {post.is_solution && <span className={styles.solutionBadge}>✓ Решение</span>}
                                                </div>
                                            </div>
                                            <div className={styles.forumPostBody}>
                                                {post.content}
                                                {post.is_edited && (
                                                    <span className={styles.editedLabel}> (ред. {formatDate(post.edited_at)})</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                        <div className={styles.scrollButtons}>
                            <button className={styles.scrollBtn} onClick={() => postsListRef.current?.scrollBy({ top: -180, behavior: 'smooth' })} {...holdUp} title="Прокрутить вверх">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
                            </button>
                            <button className={styles.scrollBtn} onClick={() => postsListRef.current?.scrollBy({ top: 180, behavior: 'smooth' })} {...holdDown} title="Прокрутить вниз">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                        </div>
                        </div>

                        {isAuthenticated && !thread?.is_locked && (
                            <div className={styles.replyForm}>
                                <textarea
                                    value={newPostContent}
                                    onChange={(e) => setNewPostContent(e.target.value)}
                                    placeholder="Напишите ответ..."
                                    rows={2}
                                    className={styles.replyTextarea}
                                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleCreatePost() } }}
                                />
                                <div className={styles.replyActions}>
                                    <EmojiPicker onSelect={(emoji) => setNewPostContent(prev => prev + emoji)} />
                                    <button
                                        onClick={handleCreatePost}
                                        className={styles.replyButton}
                                        disabled={!newPostContent.trim()}
                                    >
                                        Отправить
                                    </button>
                                </div>
                            </div>
                        )}

                        {!isAuthenticated && (
                            <div className={styles.replyForm}>
                                <button className={styles.authRequiredButton} onClick={openAuthModal}>
                                    <LockIcon size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                    Войдите, чтобы ответить
                                </button>
                            </div>
                        )}

                        {thread?.is_locked && (
                            <div className={styles.lockedNotice}>
                                <LockIcon size={18} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Эта тема заблокирована и недоступна для новых ответов
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showAuthModal && (
                <AuthModal
                    open={showAuthModal}
                    onClose={() => setShowAuthModal(false)}
                />
            )}
        </div>
    )
}
